import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLang } from '@/utils/i18n';
import { ArrowLeft, Flame, TrendingUp, Clock, Lock, ThumbsUp, ThumbsDown, MessageCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:\/\//i, 'https://');

const FeedPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [feed, setFeed] = useState([]);
  const [hotTippers, setHotTippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reactions, setReactions] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadFeed(); }, []);

  const loadFeed = async () => {
    try {
      const [feedRes, hotRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/feed/?limit=30`, { headers }).then((r) => r.json()),
        fetch(`${BACKEND_URL}/api/feed/hot-tippers`, { headers }).then((r) => r.json()),
      ]);
      setFeed(feedRes.feed || []);
      setHotTippers(hotRes.hot_tippers || []);

      // Load reactions for all feed items
      const feedItems = feedRes.feed || [];
      const reactionPromises = feedItems.slice(0, 20).map((item) =>
        fetch(`${BACKEND_URL}/api/feed/reactions/${encodeURIComponent(item.feed_key)}`, { headers })
          .then((r) => r.json())
          .then((data) => ({ key: item.feed_key, ...data }))
          .catch(() => ({ key: item.feed_key, counts: {}, my_reaction: null }))
      );
      const reactionResults = await Promise.all(reactionPromises);
      const rxMap = {};
      reactionResults.forEach((r) => { rxMap[r.key] = { counts: r.counts, my_reaction: r.my_reaction }; });
      setReactions(rxMap);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleReact = async (feedKey, reaction) => {
    try {
      await fetch(`${BACKEND_URL}/api/feed/react`, {
        method: 'POST', headers, body: JSON.stringify({ feed_key: feedKey, reaction }),
      });
      // Refresh reactions for this item
      const data = await fetch(`${BACKEND_URL}/api/feed/reactions/${encodeURIComponent(feedKey)}`, { headers }).then((r) => r.json());
      setReactions((prev) => ({ ...prev, [feedKey]: { counts: data.counts, my_reaction: data.my_reaction } }));
    } catch { /* ignore */ }
  };

  const toggleComments = async (feedKey) => {
    if (expandedComments[feedKey]) {
      setExpandedComments((prev) => ({ ...prev, [feedKey]: false }));
      return;
    }
    setExpandedComments((prev) => ({ ...prev, [feedKey]: true }));
    if (!comments[feedKey]) {
      try {
        const data = await fetch(`${BACKEND_URL}/api/feed/comments/${encodeURIComponent(feedKey)}`, { headers }).then((r) => r.json());
        setComments((prev) => ({ ...prev, [feedKey]: data.comments || [] }));
      } catch { setComments((prev) => ({ ...prev, [feedKey]: [] })); }
    }
  };

  const submitComment = async (feedKey) => {
    const text = (commentInputs[feedKey] || '').trim();
    if (!text) return;
    try {
      const data = await fetch(`${BACKEND_URL}/api/feed/comment`, {
        method: 'POST', headers, body: JSON.stringify({ feed_key: feedKey, text }),
      }).then((r) => r.json());
      setComments((prev) => ({ ...prev, [feedKey]: [...(prev[feedKey] || []), data] }));
      setCommentInputs((prev) => ({ ...prev, [feedKey]: '' }));
    } catch { /* ignore */ }
  };

  const statusColors = {
    won: 'text-green-400 bg-green-500/10 border-green-500/30',
    lost: 'text-red-400 bg-red-500/10 border-red-500/30',
    pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      return `${Math.floor(hrs / 24)}d`;
    } catch { return ''; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6] text-xl">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="feed-page">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="feed-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <h1 className="text-lg font-bold text-[#00BFA6]">{t('social_feed')}</h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Hot Tippers */}
        {hotTippers.length > 0 && (
          <div data-testid="hot-tippers">
            <h2 className="text-sm font-bold text-orange-400 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Flame className="w-4 h-4" /> {t('hot_tippers')}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {hotTippers.map((tipper, i) => (
                <div key={i} className="shrink-0 px-4 py-3 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl text-center min-w-[120px]">
                  <div className="text-sm font-bold text-white truncate">{tipper.anon_name}</div>
                  <div className="text-orange-400 font-mono font-bold text-lg flex items-center justify-center gap-1">
                    <Flame className="w-4 h-4" /> {tipper.streak}x
                  </div>
                  <div className="text-xs text-gray-500">{tipper.total_tips} Tipps</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feed */}
        <div data-testid="feed-list">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">{t('recent_bets')}</h2>

          {feed.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>{t('no_feed')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feed.map((item, i) => {
                const rx = reactions[item.feed_key] || { counts: {}, my_reaction: null };
                const isExpanded = expandedComments[item.feed_key];
                const itemComments = comments[item.feed_key] || [];

                return (
                  <Card key={i} className="bg-[#1E1E1E] border-white/10 hover:border-white/20 transition-colors" data-testid={`feed-item-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            item.is_hot ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' : 'bg-[#2A2A2A] text-gray-400'
                          }`}>
                            {item.is_hot ? <Flame className="w-5 h-5" /> : item.anon_name.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{item.anon_name}</span>
                              {item.is_hot && (
                                <span className="text-[10px] font-bold text-orange-400 bg-orange-500/20 px-1.5 py-0.5 rounded-full shrink-0" data-testid="hot-label">
                                  HOT {item.streak}x
                                </span>
                              )}
                              {item.streak > 0 && item.streak_type === 'won' && !item.is_hot && (
                                <span className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full shrink-0">{item.streak}x</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {timeAgo(item.purchased_at)}
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full border shrink-0 ${statusColors[item.status] || statusColors.pending}`}>
                          {item.status === 'won' ? 'WON' : item.status === 'lost' ? 'LOST' : 'PENDING'}
                        </span>
                      </div>

                      {/* Match info */}
                      <div className="mt-3 pl-[52px]">
                        <div className="text-sm">
                          <span className="text-white font-medium">{item.team_home}</span>
                          <span className="text-gray-500 mx-2">vs</span>
                          <span className="text-white font-medium">{item.team_away}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.sport} &middot; {item.league}</div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-400">{t('prediction')}:</span>
                          {item.prediction.endsWith('...') ? (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> {item.prediction}
                              <button onClick={() => navigate('/pricing')} className="text-[#00BFA6] hover:underline ml-1">{t('unlock')}</button>
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-[#00BFA6]">{item.prediction}</span>
                          )}
                          <span className="text-xs font-mono text-gray-400 ml-auto">{item.confidence}%</span>
                        </div>

                        {/* Reactions */}
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {[
                            { key: 'fire', icon: <Flame className="w-3.5 h-3.5" />, activeColor: 'text-orange-400 bg-orange-500/20 border-orange-500/30' },
                            { key: 'thumbsup', icon: <ThumbsUp className="w-3.5 h-3.5" />, activeColor: 'text-green-400 bg-green-500/20 border-green-500/30' },
                            { key: 'thumbsdown', icon: <ThumbsDown className="w-3.5 h-3.5" />, activeColor: 'text-red-400 bg-red-500/20 border-red-500/30' },
                          ].map((r) => {
                            const isActive = rx.my_reaction === r.key;
                            const count = rx.counts[r.key] || 0;
                            return (
                              <button
                                key={r.key}
                                data-testid={`feed-react-${r.key}-${i}`}
                                onClick={() => handleReact(item.feed_key, r.key)}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-all ${
                                  isActive ? r.activeColor : 'text-gray-500 bg-white/5 border-white/10 hover:bg-white/10'
                                }`}
                              >
                                {r.icon}
                                {count > 0 && <span className="font-mono">{count}</span>}
                              </button>
                            );
                          })}

                          <button
                            data-testid={`feed-comment-toggle-${i}`}
                            onClick={() => toggleComments(item.feed_key)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border text-gray-500 bg-white/5 border-white/10 hover:bg-white/10 transition-all ml-auto"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            {itemComments.length > 0 && <span className="font-mono">{itemComments.length}</span>}
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        </div>

                        {/* Comments section */}
                        {isExpanded && (
                          <div className="mt-3 space-y-2" data-testid={`feed-comments-${i}`}>
                            {itemComments.map((c, ci) => (
                              <div key={ci} className="bg-[#121212] rounded-lg px-3 py-2 text-xs">
                                <span className="font-medium text-[#00BFA6]">{c.anon_name}</span>
                                <span className="text-gray-500 ml-2">{timeAgo(c.created_at)}</span>
                                <p className="text-gray-300 mt-0.5">{c.text}</p>
                              </div>
                            ))}
                            <div className="flex gap-2">
                              <input
                                data-testid={`feed-comment-input-${i}`}
                                type="text"
                                value={commentInputs[item.feed_key] || ''}
                                onChange={(e) => setCommentInputs((p) => ({ ...p, [item.feed_key]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && submitComment(item.feed_key)}
                                placeholder={lang === 'de' ? 'Kommentar...' : 'Comment...'}
                                maxLength={280}
                                className="flex-1 bg-[#121212] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00BFA6]/40"
                              />
                              <button
                                data-testid={`feed-comment-send-${i}`}
                                onClick={() => submitComment(item.feed_key)}
                                className="text-[#00BFA6] hover:text-white transition-colors p-1.5"
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default FeedPage;
